import * as React from "react";
import {cx}       from "emotion";

import "./tab-previous-orders.scss";

import {consumeContext}      from "@Common/react/context";
import {ComponentLink,
        Link,
        makeChain}           from "@Common/react/cst2";
import {Load2}               from "@Common/react/load2";
import {SessionStorageState} from "@Common/react/session-storage-state";
import {UseRef}              from "@Common/react/use-ref";
import {UseState}            from "@Common/react/use-state";
import {Types}               from "@Common/types";
import {isDevXd}             from "@Common/ui-components/debug/is-dev";
import {cache0}              from "@Common/utils/cache0";

import {ErrorMessage}        from "../components/error-message";
import {GalleryImage}        from "../components/gallery-image";
import {ImportTargetArea}    from "../components/import-target-area";
import {LoadingSpinner}      from "../components/loading-spinner";
import {MultiSelectFooter}   from "../components/multi-select-footer";
import {NoticeMessage}       from "../components/notice-message";
import {Paginator}           from "../components/paginator";
import {PreviousOrderSelect} from "../components/previous-order-select";


export const TabPreviousOrders = ({
    modal,
    viewType,
    numResultsPerPage,
    imageArea                = null,
    singleSelectOptions      = null,
    multiSelectOptions       = null,
    importOptions            = null,
    orderId: currentOrderId,
    surfaceData              = null,
    isMobile,
    disallowSvgImages        = false,
    onPaginateNumResults,
}:{
    modal                    : Types.Common.UiComponents.ModalService3.ModalService3Next<Types.Common.UiComponents.PhotoDashboard.Models.PhotoDashboardData>,
    viewType                 : Types.Common.UiComponents.PhotoDashboard.viewTypes,
    numResultsPerPage        : Types.Common.UiComponents.PhotoDashboard.numResultsPerPage,
    imageArea               ?: Types.Common.UiComponents.PhotoDashboard.Models.ImageArea,
    singleSelectOptions     ?: Types.Common.UiComponents.PhotoDashboard.Models.SingleSelectOptions,
    multiSelectOptions      ?: Types.Common.UiComponents.PhotoDashboard.Models.MultiSelectOptions,
    importOptions           ?: Types.Common.UiComponents.PhotoDashboard.Models.ImportOptions,
    orderId                  : number,
    surfaceData             ?: Types.Common.Tools.CcIntegration.Models.ProductSurfaceWithImages[],
    isMobile                 : boolean,
    disallowSvgImages       ?: boolean,
    onPaginateNumResults     : Types.Common.UiComponents.PhotoDashboard.Functions.OnPaginateNumResults,
}) => (

    makeChain<JSX.Element>()

    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("templateOptions"))

    // set state vars
    .then("state", (_, next:ComponentLink<typeof UseState<{
        appliedDefaults         : boolean,
        isImageLoadingToEditor  : boolean,
        showThumbnailsOnMobile  : boolean,
        errorMessage            : string,
        pageNumber              : number,
    }>>) => UseState({
        defaultValue: {
            appliedDefaults: false,
            isImageLoadingToEditor: false,
            showThumbnailsOnMobile: false,
            errorMessage: "",
            pageNumber: 1,
        },
        next
    }))

    // set state vars to be held in browser session storage
    .then("statePersisted", (_, next:ComponentLink<typeof UseState<{
        orderId  : number,
    }>>) => SessionStorageState({
        storageKey: "previousOrderSelections",
        defaultData: {
            orderId: 0,
        },
        next,
    }))

    // display loader while applying images to the editor
    .then(({state}, next:Link<JSX.Element>) => {
        if (!state.value.isImageLoadingToEditor) return next();

        return (
            <div className="tab-content previous-order-results">
                <div className="results">
                    <LoadingSpinner text={importOptions ? "Importing Images" : "Applying Image"}/>
                </div>
            </div>
        );

    })

    // create function for applying order selection changes
    .then("onApplyOrderChange", ({state, statePersisted}, next:Link<JSX.Element, (orderId : number) => void>) => next((orderId) => {
        state.onChange({...state.value, showThumbnailsOnMobile: false, pageNumber: 1});
        statePersisted.onChange({
            ...statePersisted.value,
            orderId
        });
    }))

    // prepare loader for previous orders
    .then("loaderOrders", ({apis}, next:ComponentLink<typeof Load2<Types.Apis.Models.GalleryPreviousOrder[]>>) => Load2({
        _key: "orders",
        fetch: cache0(() => apis.next.gallery.previousOrders.getOrders()),
        captureException: true,
        next,
    }))

    // render the tab contents
    .then(({statePersisted, loaderOrders, onApplyOrderChange}, next:Link<JSX.Element>) => {

        // if the order is known, then continue to next - also validate that the order belongs to the set - the stored
        // order will not be available if changing accounts during the session
        if (statePersisted.value.orderId &&
            loaderOrders.value?.length &&
            !modal.params.adminTool &&
            loaderOrders.value?.find(x => x.id == statePersisted.value.orderId)) return next();

        return (
            <div className="tab-previous-orders-pth tab-content previous-order-banner">
                <h2>Import Photos from a Previous Order</h2>
                {modal.params.adminTool ? (
                    <h3>Feature is unavailable in admin tool</h3>
                ) : <>
                    <h3>You can retrieve photos previously used in other orders</h3>
                    {loaderOrders.loading ? (
                        <LoadingSpinner/>
                    ) : (
                        <PreviousOrderSelect
                            orders={loaderOrders.value?.filter(x => x.id !== currentOrderId)}
                            selectedOrderId={statePersisted.value.orderId}
                            useShortOrderName={false}
                            optionsWidthAsFieldWidth={isMobile}
                            onApplyOrderChange={onApplyOrderChange}
                        />
                    )}
                </>}
            </div>
        );

    })

    // prepare loader for order images
    .then("loaderOrderImages", ({apis, statePersisted}, next:ComponentLink<typeof Load2<Types.Apis.Models.GalleryImagePreviousOrder[]>>) => Load2({
        _key: "images_" + statePersisted.value.orderId,
        fetch: () => apis.next.gallery.previousOrders.getImages(statePersisted.value.orderId),
        captureException: true,
        next,
    }))

    // apply default placements from the selected order
    .then(({state, statePersisted, loaderOrders, loaderOrderImages, templateOptions}, next:Link<JSX.Element>) => {
        if (!importOptions || loaderOrders.loading || loaderOrderImages.loading || state.value.appliedDefaults) return next();
        if (isDevXd()) console.log("ImportModalTabPreviousOrders Applying defaults from order", statePersisted.value.orderId, surfaceData, loaderOrderImages.value);

        // find template IDs for current order and the previous order
        const currentTemplateIdFront = templateOptions?.sku?.front;
        const currentTemplateIdBack = templateOptions?.sku?.back;
        const previousOrderTemplateIdFront = loaderOrders.value?.find(x => x.id == statePersisted.value.orderId)?.templateIdFront;
        const previousOrderTemplateIdBack = loaderOrders.value?.find(x => x.id == statePersisted.value.orderId)?.templateIdBack;
        if (isDevXd()) console.log("ImportModalTabPreviousOrders Comparing template IDs", {currentTemplateIdFront, currentTemplateIdBack, previousOrderTemplateIdFront, previousOrderTemplateIdBack});
        if (!currentTemplateIdFront || !currentTemplateIdBack || !previousOrderTemplateIdFront || !previousOrderTemplateIdBack) return next();

        // set array to hold image load promises - using promises so that image dimensions can be found before applying
        // the image to the image area
        let updates = [];
        let imagePlacements = []; // records which target image areas were defaulted to prevent multiple defaulting

        // default images in the same positions as the previous order if template ID match
        if (currentTemplateIdFront === previousOrderTemplateIdFront && currentTemplateIdBack === previousOrderTemplateIdBack) {
            if (isDevXd()) console.log("ImportModalTabPreviousOrders Applying defaults at same image positions because template IDs match between source and current");

            // loop through all images to determine if should be defaulted
            loaderOrderImages.value.forEach(image => image.labels && image.labels.split(',').forEach(label => {

                const placementData = label.trim().split("|");
                if (!placementData || placementData.length < 2) return;

                // return if an image area doesn't exist or is already in the target position - image may not exist if using differing template styles between source and current
                const targetImageArea = surfaceData?.find(x => x.name === placementData[0])?.items.find(x => x.name === placementData[1]);
                if (!targetImageArea || targetImageArea.image) return;

                // return if an image is already queued for placement
                const imagePlacementString = placementData[0] + "|" + placementData[1];
                if (imagePlacements.indexOf(imagePlacementString) >= 0) return;
                imagePlacements.push(imagePlacementString);

                // queue the update
                updates.push(new Promise(resolve => {

                    // load dimensions
                    const imageObj = new Image();
                    imageObj.src = image.thumbnailUrl;
                    imageObj.onload = () => {
                        if (isDevXd()) console.log("ImportModalTabPreviousOrders Applying default", {placementData, image, width: imageObj.width, height: imageObj.height});

                        // apply the image
                        importOptions.onApplyImage(
                            placementData[0],
                            placementData[1],
                            image.id,
                            image.thumbnailUrl,
                            image.printUrl,
                            imageObj.width || 100,
                            imageObj.height || 100
                        );

                    }

                    resolve(image.id);

                }));

            }));

        }

        // default images based on binding indexes if not the same template and binding indexes are available
        else {
            if (isDevXd()) console.log("ImportModalTabPreviousOrders Applying defaults at binding index positions because template IDs DO NOT match between source and current");

            // loop through all images to determine if should be defaulted
            loaderOrderImages?.value.forEach(image => image.labels && image.labels.split(',').forEach(label => {

                const placementData = label.trim().split("|");
                if (!placementData || placementData.length < 3) return;

                const orderImageBindingIndex = parseInt(placementData[2].replace("binding", ""));

                // find all empty image areas matching the binding index
                surfaceData?.forEach(surface => surface.items.filter(x => (!x.image && x.bindingIndex === orderImageBindingIndex)).forEach(item => {

                    // return if an image is already queued for placement
                    const imagePlacementString = item.surfaceName + "|" + item.name;
                    if (imagePlacements.indexOf(imagePlacementString) >= 0) return;
                    imagePlacements.push(imagePlacementString);

                    // queue the update
                    updates.push(new Promise(resolve => {

                        // load dimensions
                        const imageObj = new Image();
                        imageObj.src = image.thumbnailUrl;
                        imageObj.onload = () => {
                            if (isDevXd()) console.log("ImportModalTabPreviousOrders Applying default", {item, image, width: imageObj.width, height: imageObj.height});

                            // apply the image
                            importOptions.onApplyImage(
                                item.surfaceName,
                                item.name,
                                image.id,
                                image.thumbnailUrl,
                                image.printUrl,
                                imageObj.width || 100,
                                imageObj.height || 100
                            );

                        }

                        resolve(image.id);

                    }));

                }));

            }));

        }

        // if there are any updates, apply each and then update state
        if (updates) {
            if (isDevXd()) console.log("ImportModalTabPreviousOrders Found", updates.length, "update(s) to apply");

            Promise.all(updates).then(() => {
                if (isDevXd()) console.log("ImportModalTabPreviousOrders Completed applying import defaults");
                state.onChange({...state.value, appliedDefaults: true});
            });
        }

        return next();

    })

    // allow import only if any images are selected
    .then("allowImport", (_, next:Link<JSX.Element, boolean>) => next(!!surfaceData?.find(surface => surface.items?.find(item => item.image && item.image.httpPath !== item.originalImage?.httpPath))))

    // check if pagination is required
    .then("includePagination", ({loaderOrderImages}, next:Link<JSX.Element, boolean>) => next(!importOptions && isMobile && !!loaderOrderImages.value?.length))

    // create the results area ref hook for controlling scroll position
    .then("resultsAreaRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext>) => UseRef({next}))

    // render the results
    .then(({state, statePersisted, apis, company, loaderOrders, loaderOrderImages, onApplyOrderChange, allowImport, includePagination, resultsAreaRef}) => {

        let results;
        if (loaderOrderImages.loading) results = <LoadingSpinner text="Loading Photos"/>;
        else {

            let orderImages = loaderOrderImages.value;
            if (includePagination) orderImages = orderImages.slice((state.value.pageNumber-1)*numResultsPerPage, state.value.pageNumber*numResultsPerPage);

            // add a message for empty orders - should not happen as only orders with images are available for selection
            if (!orderImages.length) results = <NoticeMessage text="There are currently no images available from this order."/>;

            // display the images when available
            else results = <>
                {orderImages.map((image) => (
                <GalleryImage
                    key={image.id}
                    id={image.id}
                    imageName={image.name}
                    imageAttributionData={image.attribution}
                    thumbnailUrl={image.thumbnailUrl}
                    viewType={viewType}
                    fileType={image.fileType}
                    fileSize={image.fileSize}
                    width={image.width}
                    height={image.height}
                    areaWidth={imageArea?.widthInches}
                    areaHeight={imageArea?.heightInches}
                    photoQualitySettings={imageArea?.photoQualitySettings}
                    allowLowQuality={company.prefs.allowLowQualityPhotosProperty}
                    disabled={image.fileType === "svg" && disallowSvgImages}
                    selectedIndex={(!multiSelectOptions) ? 0 : multiSelectOptions.selectedImages.findIndex(x => x.assetType === "order" && x.originalImageId === image.id)+1}
                    onClick={async () => {

                        // do nothing for the import process
                        if (importOptions) return;

                        // apply the image for single select mode
                        else if (singleSelectOptions) {

                            // set the image as applying
                            state.onChange({...state.value, isImageLoadingToEditor: true, errorMessage: ""});

                            // copy the image to the current order gallery
                            const savedImage = await apis.next.gallery.orders.copy({orderId: currentOrderId, imageSource: "order", imageId: image.id});
                            if (savedImage.errorMessage) {
                                state.onChange({...state.value, isImageLoadingToEditor: false, errorMessage: "There was an error loading your photo. Please try another."});
                                return;
                            }

                            // apply the image
                            singleSelectOptions.onApply(imageArea, "order", savedImage.id, savedImage.printUrl, modal.close);

                        }

                        // select the image for multi-select mode
                        else if (multiSelectOptions) {

                            // remove the current selection if already selected
                            const existingSelection = multiSelectOptions.selectedImages.find(x => x.assetType === "order" && x.originalImageId === image.id);
                            if (existingSelection) return multiSelectOptions.onSelect(existingSelection);

                            // TODO: Don't copy image until finalizing the import - This creates a delay and will create a new order gallery image for selection if they select-unselect-select-etc
                            // copy the image to the current order gallery
                            const savedImage = await apis.next.gallery.orders.copy({orderId: currentOrderId, imageSource: "order", imageId: image.id});
                            if (savedImage.errorMessage) return;

                            // select the image
                            multiSelectOptions.onSelect({
                                assetType: "order",
                                imageId: savedImage.id,
                                imagePath: savedImage.printUrl,
                                thumbnailUrl: savedImage.thumbnailUrl,
                                originalImageId: image.id,
                            });

                        }

                    }}
                    onDragStart={(e) => {

                        // do nothing if not the import process
                        if (!importOptions) return;

                        // set custom drag image
                        const dragImage = importOptions.dragImageRef.get();
                        dragImage["style"].backgroundImage = "url(\"" + image.thumbnailUrl + "\")";
                        e.persist(); // will allow the drag image to update after the background completes loading
                        e.dataTransfer.setDragImage(dragImage, dragImage.scrollWidth / 2, dragImage.scrollHeight / 2);

                        // set image data attributes on event
                        e.dataTransfer.setData("img-path-thumbnail", image.thumbnailUrl);
                        e.dataTransfer.setData("img-path-print", image.printUrl);
                        e.dataTransfer.setData("img-id", image.id.toString());

                    }}
                    onThumbnailLoadError={({currentTarget}) => {

                        // try the print url as a fallback if the thumbnail can't be loaded
                        if (currentTarget.src !== image.printUrl) {
                            currentTarget.src = image.printUrl;
                        }
                        else currentTarget.parentElement.style.display = "none";

                    }}
                />
                ))}
            </>;

        }

        const selectedOrder = loaderOrders.value?.find(x => x.id == statePersisted.value.orderId);
        if (!selectedOrder) return; // if at this step without orders, then orders are likely reloading while the order ID was already in the session state - consider adding an indicator

        return <>
            <div className={cx("tab-previous-orders-pth tab-content previous-order-results", "viewtype-"+viewType)}>
                <div className="sidebar">
                    <h2>
                        Import Photos
                    </h2>
                    <h3>
                        Retrieve photos from previous orders
                    </h3>
                    <PreviousOrderSelect
                        orders={loaderOrders.value?.filter(x => x.id !== currentOrderId)}
                        selectedOrderId={statePersisted.value.orderId}
                        useShortOrderName={true}
                        placeholderAsOption={true}
                        optionsWidthAsFieldWidth={isMobile}
                        onApplyOrderChange={onApplyOrderChange}
                    />
                    <div className="info">
                        <div className="row">
                            <span className="label" xd-qa-type="label" xd-qa-name="Created">Created</span>
                            <span className="value" xd-qa-type="text" xd-qa-name="Created Date">{(new Date(selectedOrder.dateCreated)).toLocaleDateString("en-US", {day: '2-digit', month: '2-digit', year: '2-digit'})}</span>
                        </div>
                        <hr/>
                        <div className="row">
                            <span className="label" xd-qa-type="label" xd-qa-name="Status">Status</span>
                            <span className="value" xd-qa-type="text" xd-qa-name="Status">{selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1).toLowerCase()}</span>
                        </div>
                    </div>
                    {selectedOrder.thumbnails.length > 0 && !isMobile && (
                    <p className="thumbnails">
                        {/* only display up to two thumbnails to prevent flood of thumbs from multi-surface products */}
                        {selectedOrder.thumbnails.slice(0, 2).map((item, index) => <img src={item} key={"thumb"+index} xd-qa-type="image" xd-qa-name={"Image " + index}/>)}
                    </p>
                    )}
                </div>
                <div className={cx("results", {import: !!importOptions})}
                    ref={ref => resultsAreaRef.set(ref)}>
                    <ErrorMessage text={state.value.errorMessage}/>
                    <div className="source-images">
                        {isMobile && !loaderOrderImages.loading && selectedOrder.thumbnails.length && (
                            <div className="order-thumbnails">
                                {state.value.showThumbnailsOnMobile ? (<>
                                    {/* only display up to two thumbnails to prevent flood of thumbs from multi-surface products */}
                                    {selectedOrder.thumbnails.slice(0, 2).map((item, index) => <img src={item} key={"thumb"+index} xd-qa-type="image" xd-qa-name={"Image " + index}/>)}
                                </>) : (
                                    <p className="thumbnails-toggle">
                                        <button onClick={() => state.onChange({...state.value, showThumbnailsOnMobile: true})} xd-qa-type="button" xd-qa-name="Show Product Previews">Show Product Previews</button>
                                    </p>
                                )}
                            </div>
                        )}
                        {results}
                    </div>
                    {!!importOptions && !!loaderOrderImages.value?.length && (
                        <div className="target-placements">
                            <ImportTargetArea
                                surfaceData={surfaceData}
                                onApplyImage={importOptions.onApplyImage}
                                dragImageRef={importOptions.dragImageRef}
                            />
                        </div>
                    )}
                </div>
                {includePagination && (
                    <Paginator
                        numItems={loaderOrderImages.value?.length}
                        pageNumber={state.value.pageNumber}
                        itemsPerPage={numResultsPerPage}
                        onPaginateNumResults={numResults => {
                            resultsAreaRef.get().scrollTop = 0;
                            onPaginateNumResults(numResults);
                        }}
                        onPaginatePageNumber={pageNumber => {
                            resultsAreaRef.get().scrollTop = 0;
                            state.onChange({...state.value, pageNumber});
                        }}
                    />
                )}
            </div>
            {multiSelectOptions && (
                <MultiSelectFooter
                    maxSelections={multiSelectOptions.maxSelections}
                    numSelected={multiSelectOptions.selectedImages.length}
                    onApply={() => {
                        state.onChange({...state.value, isImageLoadingToEditor: true});
                        multiSelectOptions.onApply(imageArea, multiSelectOptions.selectedImages, modal.close);
                    }}
                    onClear={multiSelectOptions.onClearSelections}
                />
            )}
            {!!importOptions && (
                <div className="footer">
                    <p>Drag images to your layout to set import positions.</p>
                    <button className="import"
                        xd-qa-type="button" xd-qa-name="Import"
                        disabled={!allowImport}
                        onClick={() => {
                            state.onChange({...state.value, isImageLoadingToEditor: true});
                            importOptions.onImport();
                        }}
                    >Import</button>
                </div>
            )}
        </>;

    })

    .run()

);
